import React from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";

import { RiExpandUpDownLine } from "@remixicon/react";
import "../BrandMetadataReview.css";
import EasyAnchorLink from "../../UI/AnchorLink";

interface TaskStep {
  title?: string;
  instructions?: (string | { text: string; sublist: string[] })[];
}

interface BrandReviewInstructionsProp {
  section_id: string;
  instruction_title?: string;
  purpose?: string;
  goal?: string;
  task_description?: string;
  task_steps?: TaskStep[];
  note?: string;
  defaultExpanded?: boolean;
  addAnchorLink?: boolean;
}

const BrandReviewInstructions: React.FC<BrandReviewInstructionsProp> = ({
  section_id,
  instruction_title,
  purpose,
  goal,
  task_description,
  task_steps = [],
  note,
  defaultExpanded = false,
  addAnchorLink = true,
}: BrandReviewInstructionsProp) => {
  return (
    <Box>
      {instruction_title && (
        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          {addAnchorLink && <EasyAnchorLink id={section_id} />}
          <Typography
            variant="h6"
            className={`${section_id}-subtitle`}
            id={section_id}
            sx={{ display: "inline-block", marginLeft: 1 }}
          >
            {instruction_title}
          </Typography>
        </Box>
      )}

      <Accordion defaultExpanded={defaultExpanded}>
        <AccordionSummary
          expandIcon={<RiExpandUpDownLine />}
          aria-controls={`${section_id}-user-instructions-content`}
          id={`${section_id}-user-instructions-header`}
        >
          <Typography variant="h6">Instructions</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <div>
            {/* Purpose */}
            {purpose && (
              <Typography paragraph>
                <strong>Purpose:</strong> {purpose}
              </Typography>
            )}

            {/* Goal */}
            {goal && (
              <Typography paragraph>
                <strong>Goal:</strong> {goal}
              </Typography>
            )}

            {/* Task Description */}
            {task_description && (
              <Typography paragraph>
                <strong>Task Description:</strong> {task_description}
              </Typography>
            )}

            {/* Task Steps */}
            {task_steps && task_steps.length > 0 ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Steps:
                </Typography>
                <ol className="metadata-list">
                  {task_steps.map((step, index) => (
                    <li key={index} className="metadata-list-item">
                      <Typography>
                        <strong>{step.title || "Untitled Step"}</strong>
                      </Typography>
                      {step.instructions && step.instructions.length > 0 && (
                        <ul className="metadata-sublist">
                          {step.instructions.map((instruction, idx) => {
                            if (typeof instruction === "string") {
                              return <li key={idx}>{instruction}</li>;
                            }
                            return (
                              <li key={idx}>
                                {instruction.text}
                                {instruction.sublist && (
                                  <ul className="metadata-sublist">
                                    {instruction.sublist.map(
                                      (detail, subIdx) => (
                                        <li key={subIdx}>{detail}</li>
                                      )
                                    )}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            ) : null}

            {/* Note */}
            {note && (
              <Typography paragraph>
                <strong>Note:</strong>{" "}
                <Box component="span" sx={{ fontStyle: "italic", p: 1 }}>
                  {note}
                </Box>
              </Typography>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default BrandReviewInstructions;
